/**
 * Chat Theme Context - Manages dynamic color theming for chat
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

interface ChatTheme {
  sidebarBg: string;
  sidebarText: string;
  sidebarHover: string;
  sidebarActive: string;
  messageBg: string;
  messageText: string;
  inputBg: string;
  inputBorder: string;
  sendButton: string;
  sendButtonHover: string;
}

/* CoreUI theme colors - https://coreui.io */
const defaultTheme: ChatTheme = {
  sidebarBg: '#ffffff',
  sidebarText: '#212631',
  sidebarHover: '#f3f4f7',
  sidebarActive: '#cfc7f3',
  messageBg: '#ffffff',
  messageText: '#212631',
  inputBg: '#ffffff',
  inputBorder: '#dbdfe6',
  sendButton: '#5856d6',
  sendButtonHover: '#3634a3',
};

interface ChatThemeContextType {
  theme: ChatTheme;
  setTheme: (theme: Partial<ChatTheme>) => void;
  resetTheme: () => void;
}

const ChatThemeContext = createContext<ChatThemeContextType>({
  theme: defaultTheme,
  setTheme: () => {},
  resetTheme: () => {},
});

export const useChatTheme = () => {
  const context = useContext(ChatThemeContext);
  if (!context) {
    throw new Error('useChatTheme must be used within ChatThemeProvider');
  }
  return context;
};

interface ChatThemeProviderProps {
  children: React.ReactNode;
}

export const ChatThemeProvider: React.FC<ChatThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<ChatTheme>(() => {
    // Load from localStorage if available
    const saved = localStorage.getItem('chat-theme');
    if (saved) {
      try {
        return { ...defaultTheme, ...JSON.parse(saved) };
      } catch {
        return defaultTheme;
      }
    }
    return defaultTheme;
  });

  useEffect(() => {
    // Save to localStorage
    localStorage.setItem('chat-theme', JSON.stringify(theme));
    
    // Apply CSS variables
    const root = document.documentElement;
    root.style.setProperty('--chat-sidebar-bg', theme.sidebarBg);
    root.style.setProperty('--chat-sidebar-text', theme.sidebarText);
    root.style.setProperty('--chat-sidebar-hover', theme.sidebarHover);
    root.style.setProperty('--chat-sidebar-active', theme.sidebarActive);
    root.style.setProperty('--chat-message-bg', theme.messageBg);
    root.style.setProperty('--chat-message-text', theme.messageText);
    root.style.setProperty('--chat-input-bg', theme.inputBg);
    root.style.setProperty('--chat-input-border', theme.inputBorder);
    root.style.setProperty('--chat-send-button', theme.sendButton);
    root.style.setProperty('--chat-send-button-hover', theme.sendButtonHover);
  }, [theme]);

  const setTheme = (newTheme: Partial<ChatTheme>) => {
    setThemeState(prev => ({ ...prev, ...newTheme }));
  };

  const resetTheme = () => {
    setThemeState(defaultTheme);
  };

  return (
    <ChatThemeContext.Provider value={{ theme, setTheme, resetTheme }}>
      {children}
    </ChatThemeContext.Provider>
  );
};

